import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import dict from "./dict";
import logger from "@/lib/logger";

const LangContext = createContext(null);

const LANG_KEY = "mordomo_lang";

const safeGet = () => {
  try {
    const v = localStorage.getItem(LANG_KEY);
    return v === "en" || v === "pt" ? v : "pt";
  } catch (err) {
    logger.debug("[lang] localStorage unavailable:", err?.message);
    return "pt";
  }
};

const safeSet = (v) => {
  try {
    localStorage.setItem(LANG_KEY, v);
  } catch (err) {
    logger.debug("[lang] localStorage.setItem unavailable:", err?.message);
  }
};

export function LangProvider({ children }) {
  const [lang, setLang] = useState(safeGet);

  useEffect(() => {
    safeSet(lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (path, replacements = {}) => {
      const parts = path.split(".");
      let cur = dict[lang];
      for (const p of parts) {
        if (cur == null) return path;
        cur = cur[p];
      }
      if (typeof cur === "string") {
        return cur.replace(/\{(\w+)\}/g, (_, k) =>
          replacements[k] != null ? replacements[k] : `{${k}}`
        );
      }
      return cur ?? path;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be inside LangProvider");
  return ctx;
}
